import style from "@/styles/pages/Amortization/Amortization.module.css";
import { FormProvider, useForm } from "react-hook-form";
import clsx from "clsx";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "@/store/dtm";
import { setAmortizationLength, setApplicationProgress } from "@/slices/applicationSlice";
import SlideTransition from "@/styles/animation/SlideTransition";
import { createApiClient } from "@/lib/Requests";
import { ApplicationDto } from "@/lib/DTMWebAPIClient.generated";
import { applicationCategory } from "@/types/applicationType";
import { setClickInsightApplicationProgress, setClickInsightEvents, setClickInsightFormErrors } from "@/lib/util";

export default function Amortization() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const applicant = useSelector(
        (state: RootState) => state.applicant.applicant
    );
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

    const methods = useForm({
        defaultValues: {
            amortizationLength: ""
        },
    });

    useEffect(() => {
        document.title = "Select Amortization";
        methods.setValue("amortizationLength", application ? application.amortizationLength == 0 ? "" : application.amortizationLength?.toString() : "");
        setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);

        client.apiApplicationsFetch().then((result) => {
            result.application = application;
            client.apiApplicationsSave(new ApplicationDto({
                applicationData: JSON.stringify(result)
            })).catch((error) => {
                console.log("Error saving application info");
            });
        }).catch((error) => {
            console.log("Error fetching application info");
        });
    }, []);

    const onError = (errors: object) => {
        setClickInsightFormErrors(errors);
    }

    const onSubmit = (data: any) => {
        dispatch(setAmortizationLength(data.amortizationLength != "unknown" ? +data.amortizationLength : "unknown"));
        dispatch(setApplicationProgress(6));
        setClickInsightApplicationProgress('Your new mortgage - What amortization would you like?');
        switch (application.applicationCategory) {
            case applicationCategory.Buy:
                navigate("/single/closing-date");
                break;
            case applicationCategory.Refinance:
            case applicationCategory.Transfer:
                navigate("/single/funding-date");
                break;
        }
    };

    return (
        <>
            <SlideTransition>
                <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
                    <div
                        className={style.addHeading}
                        role="region"
                        aria-label="Address Header"
                    >
                        <div className="max-md:ml-2">
                            <h1 aria-label="Address Header">What amortization would you like?</h1>
                            <p className="sub-heading">This is how long you'll have to pay off your mortgage in full.</p>
                        </div>
                        <div className={"flex flex-row mt-5"}>
                            <FormProvider {...methods}>
                                <form
                                    autoComplete="off"
                                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                                >
                                    <div className="flex md:flex-row flex-col w-max">
                                        <div className="mt-5">
                                            <label htmlFor="politicallyExposedType">
                                                Amortization
                                            </label>
                                            <div className="flex relative">
                                                <select
                                                    defaultValue=""
                                                    {...methods.register("amortizationLength", {
                                                        required: "Please select an option"
                                                    })}
                                                    className={clsx(
                                                        "pt-2.5 mt-3"
                                                    )}
                                                    id="amortizationLength"
                                                >
                                                    <option hidden value="">
                                                        Choose...
                                                    </option>
                                                    <option value="1">1 Year</option>
                                                    <option value="5">5 Years</option>
                                                    <option value="10">10 Years</option>
                                                    <option value="15">15 Years</option>
                                                    <option value="20">20 Years</option>
                                                    <option value="25">25 Years</option>
                                                    <option value="30">30 Years</option>
                                                    <option value="unknown">I don't know</option>
                                                </select>
                                            </div>
                                            {methods.formState?.errors?.amortizationLength
                                                ?.message && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {
                                                            methods.formState?.errors
                                                                ?.amortizationLength?.message
                                                        }
                                                    </span>
                                                )}
                                        </div>
                                    </div>

                                    <div className="flex max-sm:hidden">
                                        <div className="mb-5 mr-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-169 sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                            >
                                                Continue
                                            </button>
                                        </div>
                                    </div>
                                    <div className="sm:hidden">
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                            >
                                                Continue
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-full sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </SlideTransition>
        </>
    );
}